import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import {
  Close,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import VisitTimeField from './VisitTimeField';
import AppDateField from './AppDateField';
import UiIncidentFields from './UiIncidentFields';
import Transportation from '../pages/Transportation';
import { api } from '../services/api';
import {
  Client,
  Service,
  Provider,
  Visit,
  VisitCreate,
  VisitUpdate,
  VisitParentWithVisits,
  Transportation as TransportationType,
  TransportationParentWithTransportations,
} from '../types';
import {
  parseFlexibleTimeInput,
  toMysqlDateTime,
  scheduleKey,
  formatTimeAmPmFromScheduled,
  formatTimeAmPmFromDate,
  easternTodayYmd,
  easternWallClockDate,
} from '../utils/visitTime';
import { runVisitPayloadOverlapCheck, runSingleVisitOverlapCheck } from '../utils/scheduleOverlap';
import {
  emptyUiDraft,
  uiDraftFromRecord,
  uiDraftHasContent,
  validateUiDraft,
  syncUiForVisit,
  UiDraft,
  UiApiLike,
} from '../utils/ui';

const adminUiApi: UiApiLike = {
  list: async params => api.ui.list(params),
  create: async data => api.ui.create(data),
  update: async (id, data) => api.ui.update(id, data),
  delete: async id => {
    await api.ui.delete(id);
  },
};

const LINE_BG = '#2D4B73';
const GREEN_BTN = '#76ad4b';

const actionBtnSx = (filled: boolean) => ({
  minWidth: 88,
  borderRadius: 2,
  textTransform: 'none' as const,
  fontWeight: 600,
  boxShadow: 'none',
  // Match transportation: blue for Add, green once data is entered (Change/Edit)
  bgcolor: filled ? GREEN_BTN : LINE_BG,
  '&:hover': { bgcolor: filled ? '#5f9439' : '#1E3452', boxShadow: 'none' },
});

export type DraftClientLine = {
  visit_id?: number;
  client_id: number | '';
  service_id: number | '';
  isp_id: number | '';
  outcome_id: number | '';
  experience_id: number | '';
  support_id: number | '';
  support_ids: number[];
  visit_type: string;
  behavior_notes: string;
  behaviorExpanded: boolean;
  unusual_incident_flag: boolean;
  unusual_incident_documentation: string;
  unusual_incident_injury: string;
  unusual_incident_actions_taken: string;
  unusual_incident_cause: string;
  major_unusual_incident_flag: boolean;
  ui: UiDraft;
};

type VisitRowPayload = VisitCreate & { visit_id?: number; ui?: UiDraft };

const emptyDraftRow = (): DraftClientLine => ({
  client_id: '',
  service_id: '',
  isp_id: '',
  outcome_id: '',
  experience_id: '',
  support_id: '', support_ids: [],
  visit_type: '',
  behavior_notes: '',
  behaviorExpanded: false,
  unusual_incident_flag: false,
  unusual_incident_documentation: '',
  unusual_incident_injury: '',
  unusual_incident_actions_taken: '',
  unusual_incident_cause: '',
  major_unusual_incident_flag: false,
  ui: emptyUiDraft(),
});

export type VisitSegmentState = {
  startTime: string;
  endTime: string;
  clients: DraftClientLine[];
};

interface GroupVisitFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  fixedProviderId?: number | null;
  providers: Provider[];
  defaultProviderId?: number | null;
  editingVisit: Visit | null;
}

type VisitTransportationLink = {
  visitIdByClientId: Record<number, number>;
  providerId: number;
  transportationDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  clientIds?: number[];
};

const buildVisitTransportLink = (
  rows: {
    client_id?: number | string;
    provider_id?: number | string;
    visit_date?: string;
    scheduled_start?: string;
    scheduled_end?: string;
  }[],
  visitIds: (number | undefined | null)[]
): VisitTransportationLink | null => {
  if (!rows.length) return null;
  const visitIdByClientId: Record<number, number> = {};
  rows.forEach((r, i) => {
    const cid = Number(r.client_id);
    const vid = visitIds[i] != null ? Number(visitIds[i]) : 0;
    if (cid > 0 && vid > 0 && visitIdByClientId[cid] == null) {
      visitIdByClientId[cid] = vid;
    }
  });
  if (Object.keys(visitIdByClientId).length === 0) return null;
  const first = rows[0];
  const date =
    first.visit_date ||
    (first.scheduled_start ? String(first.scheduled_start).split(/[T ]/)[0] : '') ||
    '';
  return {
    visitIdByClientId,
    providerId: Number(first.provider_id),
    transportationDate: date,
    scheduledStart: first.scheduled_start,
    scheduledEnd: first.scheduled_end,
    clientIds: Object.keys(visitIdByClientId).map(Number),
  };
};

type IspBundle = {
  outcomes: { outcome_id: number; isp_id?: number; outcome_title?: string; active_status?: boolean }[];
  experiences: {
    experience_id: number;
    isp_id?: number;
    outcome_id?: number;
    experience_title?: string;
    active_status?: boolean;
  }[];
  supports: {
    support_id: number;
    isp_id?: number;
    support_title?: string;
    isp_service_id?: number;
    active_status?: boolean;
  }[];
  ispServices: { isp_id: number; service_id: number; isp_service_id: number }[];
};

/** Prefer an active ISP for the visit date (newest first from the server). */
const defaultActiveIspId = (
  isps: { isp_id: number; active_status?: boolean }[]
): number | '' => {
  const active = isps.find(isp => isp.active_status !== false);
  if (active) return active.isp_id;
  return isps.length > 0 ? isps[0].isp_id : '';
};

const durationSeconds = (start: string, end: string): number | undefined => {
  if (!start || !end) return undefined;
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return undefined;
  return Math.max(0, Math.floor((b.getTime() - a.getTime()) / 1000));
};

const visitHours = (start: string, end: string): number => {
  const d = durationSeconds(start, end);
  return d != null ? d / 3600 : 0;
};

const readWeeklyTotal = (wh: unknown): number => {
  if (wh && typeof wh === 'object') {
    const w = wh as { total_hours?: number; data?: { total_hours?: number } };
    if (typeof w.total_hours === 'number') return w.total_hours;
    if (w.data && typeof w.data.total_hours === 'number') return w.data.total_hours;
  }
  return 0;
};

const formatDateForMySQL = (date: Date | string): string => {
  if (typeof date === 'string') {
    const normalized = date.trim().replace(' ', 'T');
    const [datePart, timePart = '00:00:00'] = normalized.split('T');
    const [hours = '00', minutes = '00', seconds = '00'] = timePart.split(':');
    return `${datePart} ${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0').slice(0, 2)}`;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

function lineSupportIds(v: { support_ids?: number[]; support_id?: number | string | null }): number[] {
  if (Array.isArray(v.support_ids) && v.support_ids.length > 0) {
    return v.support_ids.map(Number).filter(id => id > 0);
  }
  const legacy = v.support_id === '' || v.support_id == null ? 0 : Number(v.support_id);
  return legacy > 0 ? [legacy] : [];
}

function deriveIspId(
  row: DraftClientLine,
  bundle: IspBundle | undefined
): number {
  const rawIsp = row.isp_id;
  if (rawIsp !== '' && rawIsp !== undefined) {
    const n = Number(rawIsp);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const selectedSupportId = row.support_ids?.[0] || row.support_id;
  if (selectedSupportId !== '' && selectedSupportId !== undefined && bundle) {
    const s = bundle.supports.find(x => x.support_id === selectedSupportId);
    if (s?.isp_id) return s.isp_id;
  }
  if (row.experience_id !== '' && row.experience_id !== undefined && bundle) {
    const e = bundle.experiences.find(x => x.experience_id === row.experience_id);
    if (e?.isp_id) return e.isp_id;
  }
  if (row.outcome_id !== '' && row.outcome_id !== undefined && bundle) {
    const o = bundle.outcomes.find(x => x.outcome_id === row.outcome_id);
    if (o?.isp_id) return o.isp_id as number;
  }
  return 0;
}

const hasServices = (row: DraftClientLine, bundle?: IspBundle): boolean => {
  if (row.client_id === '' || row.service_id === '') return false;
  return deriveIspId(row, bundle) > 0;
};

const hasBehavior = (row: DraftClientLine): boolean => {
  if (row.behavior_notes?.trim()) return true;
  if (uiDraftHasContent(row.ui)) return true;
  return false;
};

const GroupVisitForm: React.FC<GroupVisitFormProps> = ({
  open,
  onClose,
  onSaved,
  fixedProviderId,
  providers,
  defaultProviderId,
  editingVisit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [showServiceErrors, setShowServiceErrors] = useState(false);
  const [showBehaviorErrors, setShowBehaviorErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const savingRef = useRef(false);
  const [providerId, setProviderId] = useState<number | ''>('');
  const [visitDate, setVisitDate] = useState('');
  const [status, setStatus] = useState('COMPLETED');
  const [headerNotes, setHeaderNotes] = useState('');
  const [segments, setSegments] = useState<VisitSegmentState[]>([
    { startTime: '', endTime: '', clients: [] },
  ]);

  const [clients, setClients] = useState<Client[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [ispBundleByIsp, setIspBundleByIsp] = useState<Record<number, IspBundle>>({});
  const [serviceByClient, setServiceByClient] = useState<Record<number, Service[]>>({});
  const [txIdSetByClient, setTxIdSetByClient] = useState<Record<number, Set<number>>>({});
  const [ispsByClient, setIspsByClient] = useState<
    Record<
      number,
      {
        isp_id: number;
        description?: string;
        span_start_date?: string;
        span_end_date?: string;
        active_status?: boolean;
      }[]
    >
  >({});

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addClientPick, setAddClientPick] = useState<number | ''>('');
  const [servicesDialog, setServicesDialog] = useState<{ clientIndex: number } | null>(null);
  const [behaviorDialog, setBehaviorDialog] = useState<{ clientIndex: number } | null>(null);
  const [panelDraft, setPanelDraft] = useState<DraftClientLine>(emptyDraftRow());
  const [multiSegmentWarning, setMultiSegmentWarning] = useState(false);

  const [splitOpen, setSplitOpen] = useState(false);
  const [splitConfirmOpen, setSplitConfirmOpen] = useState(false);
  const [otOpen, setOtOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [pendingSingleCreate, setPendingSingleCreate] = useState<VisitCreate | null>(null);
  const [pendingServiceId, setPendingServiceId] = useState<number | null>(null);
  const [transportDraft, setTransportDraft] = useState<Record<string, unknown>[] | null>(null);
  const [linkedTransportParentId, setLinkedTransportParentId] = useState<number | null>(null);
  const [transportDialogOpen, setTransportDialogOpen] = useState(false);
  const [transportSession, setTransportSession] = useState(0);
  const [transportPrefill, setTransportPrefill] = useState<VisitTransportationLink | null>(null);

  const effectiveProvider = fixedProviderId ?? (providerId === '' ? 0 : Number(providerId));

  const isOvertimeService = (serviceId: number) =>
    allServices.some(s => s.ot_id != null && Number(s.ot_id) === serviceId);

  const ensureIspBundle = useCallback(async (ispId: number) => {
    if (!ispId) return;
    try {
      const [outcomesRes, experiencesRes, supportsRes, ispServicesRes] = await Promise.all([
        api.clientISP.outcomes.list(ispId),
        api.clientISP.experiences.list(ispId),
        api.clientISP.supports.list(ispId),
        api.clientIspServices.getByIsp(ispId),
      ]);
      setIspBundleByIsp(prev => ({
        ...prev,
        [ispId]: {
          outcomes: Array.isArray(outcomesRes) ? outcomesRes : [],
          experiences: Array.isArray(experiencesRes) ? experiencesRes : [],
          supports: Array.isArray(supportsRes) ? supportsRes : [],
          ispServices: Array.isArray(ispServicesRes) ? ispServicesRes : [],
        },
      }));
    } catch {
      setIspBundleByIsp(prev => ({
        ...prev,
        [ispId]: { outcomes: [], experiences: [], supports: [], ispServices: [] },
      }));
    }
  }, []);

  const loadClientDeps = useCallback(
    async (clientId: number) => {
      if (!clientId || !effectiveProvider) return;
      if (!serviceByClient[clientId]) {
        try {
          const r = await api.clientIspServices.getServicesForClientProvider(clientId, effectiveProvider);
          const list = Array.isArray(r) ? r : (r as { items?: Service[] })?.items || [];
          setServiceByClient(prev => ({ ...prev, [clientId]: list }));
        } catch {
          setServiceByClient(prev => ({ ...prev, [clientId]: [] }));
        }
      }
      if (!txIdSetByClient[clientId]) {
        try {
          const t = await api.clientIspServices.getTransportationServicesForClientProvider(clientId, effectiveProvider);
          const arr = Array.isArray(t) ? t : [];
          setTxIdSetByClient(prev => ({
            ...prev,
            [clientId]: new Set(arr.map((s: Service) => Number(s.service_id))),
          }));
        } catch {
          setTxIdSetByClient(prev => ({ ...prev, [clientId]: new Set() }));
        }
      }
    },
    [effectiveProvider, serviceByClient, txIdSetByClient]
  );

  const fetchIspsForClientRow = useCallback(
    async (clientId: number) => {
      if (!effectiveProvider || !clientId) return;
      const key = visitDate || easternTodayYmd();
      try {
        const r = await api.clientISP.listForVisit(effectiveProvider, clientId, key);
        const isps = Array.isArray(r) ? r : [];
        setIspsByClient(prev => ({ ...prev, [clientId]: isps }));
      } catch {
        setIspsByClient(prev => ({ ...prev, [clientId]: [] }));
      }
    },
    [effectiveProvider, visitDate]
  );

  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  useEffect(() => {
    if (!open) return;
    api.services.list(0, 500).then(r => {
      const a = Array.isArray(r) ? r : (r as { items?: Service[] })?.items || [];
      setAllServices(a);
    });
  }, [open]);

  const resetToNew = useCallback(() => {
    setError(null);
    setShowErrors(false);
    setShowServiceErrors(false);
    setShowBehaviorErrors(false);
    setHeaderNotes('');
    setVisitDate(easternTodayYmd());
    setStatus('COMPLETED');
    setSegments([{ startTime: '', endTime: '', clients: [] }]);
    setIspBundleByIsp({});
    setServiceByClient({});
    setTxIdSetByClient({});
    setIspsByClient({});
    setMultiSegmentWarning(false);
    setAddClientOpen(false);
    setServicesDialog(null);
    setBehaviorDialog(null);
    setTransportDraft(null);
    setLinkedTransportParentId(null);
    setTransportDialogOpen(false);
    setTransportPrefill(null);
    if (fixedProviderId) {
      setProviderId(fixedProviderId);
    } else {
      setProviderId(defaultProviderId && defaultProviderId > 0 ? defaultProviderId : '');
    }
  }, [defaultProviderId, fixedProviderId]);

  useEffect(() => {
    if (!open) return;
    if (!editingVisit) {
      resetToNew();
    }
  }, [open, editingVisit, resetToNew]);

  // Hydrate edit form only when the dialog opens / the visit changes — not when
  // visitDate (or ISP fetch callbacks) change, which would wipe the user's date edit.
  useEffect(() => {
    if (!open || !editingVisit) return;
    let cancel = false;
    const run = async () => {
      try {
        setLoading(true);
        const parent = (await api.visitParents.get(editingVisit.visit_parent_id)) as VisitParentWithVisits;
        if (cancel) return;
        const rows = [...(parent.visits || [])].sort((a, b) => Number(a.visit_id) - Number(b.visit_id));
        if (rows.length === 0) {
          resetToNew();
          return;
        }
        setProviderId(Number(parent.provider_id));
        const d0 = rows[0].scheduled_start?.split('T')[0] || rows[0].scheduled_start?.split(' ')[0] || '';
        setVisitDate(d0);
        setStatus(rows[0].status || 'COMPLETED');
        setHeaderNotes(rows[0].notes || '');

        const bySeg = new Map<string, Visit[]>();
        for (const v of rows) {
          const k = `${scheduleKey(v.scheduled_start)}|${scheduleKey(v.scheduled_end)}`;
          if (!bySeg.has(k)) bySeg.set(k, []);
          bySeg.get(k)!.push(v);
        }
        const keys = Array.from(bySeg.keys()).sort((ka, kb) => {
          const a = bySeg.get(ka)![0].scheduled_start || '';
          const b = bySeg.get(kb)![0].scheduled_start || '';
          return a.localeCompare(b);
        });
        const segs: VisitSegmentState[] = keys.map(k => {
          const group = bySeg.get(k)!.sort((a, b) => Number(a.visit_id) - Number(b.visit_id));
          const s = group[0].scheduled_start;
          const e = group[0].scheduled_end;
          return {
            startTime: formatTimeAmPmFromScheduled(s || ''),
            endTime: formatTimeAmPmFromScheduled(e || ''),
            clients: group.map(
              (v): DraftClientLine => ({
                visit_id: v.visit_id,
                client_id: v.client_id != null ? Number(v.client_id) : '',
                service_id: v.service_id != null ? Number(v.service_id) : '',
                isp_id: v.isp_id != null ? Number(v.isp_id) : '',
                outcome_id: v.outcome_id != null ? Number(v.outcome_id) : '',
                experience_id: v.experience_id != null ? Number(v.experience_id) : '',
                support_id: lineSupportIds(v)[0] ?? '',
                support_ids: lineSupportIds(v),
                visit_type: v.visit_type || '',
                behavior_notes: v.behavior_notes || '',
                behaviorExpanded: false,
                unusual_incident_flag: v.unusual_incident_flag || false,
                unusual_incident_documentation: v.unusual_incident_documentation || '',
                unusual_incident_injury: v.unusual_incident_injury || '',
                unusual_incident_actions_taken: v.unusual_incident_actions_taken || '',
                unusual_incident_cause: v.unusual_incident_cause || '',
                major_unusual_incident_flag: v.major_unusual_incident_flag || false,
                ui: emptyUiDraft(),
              })
            ),
          };
        });
        for (const seg of segs) {
          for (const clientLine of seg.clients) {
            if (!clientLine.visit_id) continue;
            try {
              const uiRecords = await adminUiApi.list({ visit_id: clientLine.visit_id });
              if (uiRecords.length > 0) {
                clientLine.ui = uiDraftFromRecord(uiRecords[0]);
              }
            } catch {
              /* optional MUI load */
            }
          }
        }
        setSegments(keys.length > 1 ? [segs[0]] : segs);
        setMultiSegmentWarning(keys.length > 1);

        // Prefetch services / ISP bundles for editor
        const pId = parent.provider_id;
        const cids = Array.from(new Set(rows.map(r => Number(r.client_id))));
        const cl = await api.clients.listByProvider(pId, 500);
        setClients(Array.isArray(cl) ? cl : (cl as { items?: Client[] })?.items || []);
        for (const cid of cids) {
          await loadClientDeps(cid);
          await fetchIspsForClientRow(cid);
          const row = rows.find(r => r.client_id === cid);
          if (row?.isp_id) await ensureIspBundle(row.isp_id);
        }

        // Load transportation linked to any visit in this occurrence.
        const visitIdSet = new Set(rows.map(r => Number(r.visit_id)).filter(id => id > 0));
        if (visitIdSet.size > 0) {
          try {
            const occResp = await api.transportationParents.list(0, 500, { providerId: Number(pId) });
            const occurrences = (occResp?.items || []) as TransportationParentWithTransportations[];
            let linkedParent: TransportationParentWithTransportations | null = null;
            for (const occ of occurrences) {
              const match = (occ.transportations || []).some(
                t => t.visit_id != null && visitIdSet.has(Number(t.visit_id))
              );
              if (match) {
                linkedParent = occ;
                break;
              }
            }
            if (linkedParent) {
              const full = (await api.transportationParents.get(
                linkedParent.transportation_parent_id
              )) as TransportationParentWithTransportations;
              const txRows = [...(full.transportations || [])].sort(
                (a, b) => Number(a.transportation_id) - Number(b.transportation_id)
              );
              if (txRows.length > 0) {
                setLinkedTransportParentId(Number(full.transportation_parent_id));
                setTransportDraft(
                  txRows.map((r: TransportationType) => ({
                    provider_id: r.provider_id,
                    client_id: r.client_id,
                    transportation_dim_id: r.transportation_dim_id ?? null,
                    service_id: r.service_id,
                    start_location_id: r.start_location_id,
                    end_location_id: r.end_location_id,
                    transportation_date: r.transportation_date,
                    scheduled_start: r.scheduled_start,
                    scheduled_end: r.scheduled_end,
                    miles: r.miles,
                    details: r.details ?? null,
                    visit_id: r.visit_id ?? null,
                    ratio: r.ratio,
                    starting_miles: r.starting_miles,
                    ending_miles: r.ending_miles,
                    notes: r.notes ?? null,
                    transportation_id: r.transportation_id,
                  }))
                );
              }
            } else {
              setLinkedTransportParentId(null);
              setTransportDraft(null);
            }
          } catch {
            /* optional linked transport load */
          }
        }
      } catch {
        if (!cancel) setError('Failed to load visit occurrence.');
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancel = true;
    };
    // Intentionally omit fetchIspsForClientRow / loadClientDeps: they change with visitDate
    // and would re-hydrate (resetting visitDate) whenever the user edits the date.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate only on open/editingVisit
  }, [open, editingVisit]);

  // Refresh ISP options for clients already on the form when the visit date changes.
  useEffect(() => {
    if (!open || !visitDate || !effectiveProvider) return;
    const cids = Array.from(
      new Set(
        segmentsRef.current
          .flatMap(s => s.clients)
          .map(c => (c.client_id === '' ? 0 : Number(c.client_id)))
          .filter(id => id > 0)
      )
    );
    for (const cid of cids) {
      void fetchIspsForClientRow(cid);
    }
  }, [open, visitDate, effectiveProvider, fetchIspsForClientRow]);

  useEffect(() => {
    if (!open || !effectiveProvider) {
      setClients([]);
      return;
    }
    api.clients
      .listByProvider(effectiveProvider, 500)
      .then(r => setClients(Array.isArray(r) ? r : (r as { items?: Client[] })?.items || []))
      .catch(() => setClients([]));
  }, [open, effectiveProvider]);

  const nonTxForClient = (cid: number) => {
    const list = serviceByClient[cid] || [];
    const tx = txIdSetByClient[cid] || new Set();
    return list.filter(s => !tx.has(Number(s.service_id)));
  };

  const visitClients = segments[0]?.clients ?? [];
  const seg = segments[0] ?? { startTime: '', endTime: '', clients: [] };
  const providerMissing = showErrors && !effectiveProvider;
  const dateMissing = showErrors && !visitDate;
  const clientsMissing = showErrors && visitClients.length === 0;
  const startTimeInvalid = showErrors && !parseFlexibleTimeInput(seg.startTime);
  const endTimeResult = parseFlexibleTimeInput(seg.endTime);
  const startTimeResult = parseFlexibleTimeInput(seg.startTime);
  let endTimeMessage: string | undefined;
  if (showErrors) {
    if (!endTimeResult) {
      endTimeMessage = seg.endTime.trim() ? 'Enter a valid end time' : 'End time is required';
    } else if (startTimeResult && visitDate) {
      const ss = toMysqlDateTime(visitDate, startTimeResult);
      const ee = toMysqlDateTime(visitDate, endTimeResult);
      if (ss && ee && new Date(ee) <= new Date(ss)) {
        endTimeMessage = 'End time must be after start time';
      }
    }
  }

  const hasTransportation = !!transportDraft?.length;
  const transportSummary = (() => {
    if (!transportDraft?.length) return null;
    const clientIds = Array.from(
      new Set(transportDraft.map(r => Number(r.client_id)).filter(id => id > 0))
    );
    const names = clientIds
      .map(id => {
        const cl = clients.find(x => Number(x.client_id) === id);
        return cl ? `${cl.first_name} ${cl.last_name}` : `Client ${id}`;
      })
      .join(', ');
    const startIds = new Set(
      transportDraft.map(r => Number(r.start_location_id)).filter(id => id > 0)
    );
    const endIds = new Set(
      transportDraft.map(r => Number(r.end_location_id)).filter(id => id > 0)
    );
    const stopCount = new Set([...Array.from(startIds), ...Array.from(endIds)]).size;
    const miles = transportDraft.reduce((sum, r, i, arr) => {
      // Prefer one miles value per unique start-end leg (first passenger on that leg).
      const key = `${r.start_location_id}-${r.end_location_id}`;
      if (arr.findIndex(x => `${x.start_location_id}-${x.end_location_id}` === key) !== i) {
        return sum;
      }
      const m = r.miles != null ? Number(r.miles) : NaN;
      return Number.isFinite(m) ? sum + m : sum;
    }, 0);
    const parts = [
      names || null,
      stopCount > 0 ? `${stopCount} stop${stopCount === 1 ? '' : 's'}` : null,
      miles > 0 ? `${miles} mi` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'Transportation entered';
  })();

  const clientName = (id: number | ''): string => {
    if (id === '') return '—';
    const cl = clients.find(x => Number(x.client_id) === Number(id));
    return cl ? `${cl.first_name} ${cl.last_name}` : `Client ${id}`;
  };

  const providerLabel = (() => {
    if (!fixedProviderId) return '';
    const p = providers.find(x => Number(x.provider_id) === Number(fixedProviderId));
    return p ? `${p.first_name} ${p.last_name}` : '';
  })();

  const setStartTime = (v: string) => {
    setSegments(prev => [{ ...(prev[0] ?? { startTime: '', endTime: '', clients: [] }), startTime: v }]);
  };

  const setEndTime = (v: string) => {
    setSegments(prev => [{ ...(prev[0] ?? { startTime: '', endTime: '', clients: [] }), endTime: v }]);
  };

  const setTimeToNow = (field: 'start' | 'end') => {
    const now = easternWallClockDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    setVisitDate(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
    const timeStr = formatTimeAmPmFromDate(now);
    if (field === 'start') {
      setStartTime(timeStr);
    } else {
      setEndTime(timeStr);
    }
  };

  const updatePanelDraft = (patch: Partial<DraftClientLine>) => {
    setPanelDraft(prev => ({ ...prev, ...patch }));
  };

  const openAddClient = () => {
    setAddClientPick('');
    setAddClientOpen(true);
    setError(null);
  };

  const confirmAddClient = () => {
    if (addClientPick === '') {
      setError('Select a client.');
      return;
    }
    const cid = Number(addClientPick);
    if (visitClients.some(c => c.client_id !== '' && Number(c.client_id) === cid)) {
      setError('This client is already on this visit.');
      return;
    }
    setSegments(prev => [
      {
        ...(prev[0] ?? { startTime: '', endTime: '', clients: [] }),
        clients: [...(prev[0]?.clients ?? []), { ...emptyDraftRow(), client_id: cid }],
      },
    ]);
    void loadClientDeps(cid);
    void fetchIspsForClientRow(cid);
    setAddClientOpen(false);
    setAddClientPick('');
    setError(null);
  };

  const removeClient = (index: number) => {
    setSegments(prev => [
      {
        ...(prev[0] ?? { startTime: '', endTime: '', clients: [] }),
        clients: (prev[0]?.clients ?? []).filter((_, i) => i !== index),
      },
    ]);
  };

  const openServicesDialog = (index: number) => {
    const row = visitClients[index];
    if (!row) return;
    setPanelDraft({ ...row });
    setServicesDialog({ clientIndex: index });
    setError(null);
    const cid = Number(row.client_id);
    if (cid) {
      void loadClientDeps(cid);
      void fetchIspsForClientRow(cid);
      if (row.isp_id !== '' && row.isp_id !== undefined) void ensureIspBundle(Number(row.isp_id));
    }
  };

  // Auto-select the client's current active ISP when adding/editing services.
  useEffect(() => {
    if (!servicesDialog) return;
    const row = visitClients[servicesDialog.clientIndex];
    if (!row || row.client_id === '') return;
    const cid = Number(row.client_id);
    const isps = ispsByClient[cid] || [];
    if (isps.length === 0) return;

    const currentIspId = panelDraft.isp_id;
    const hasValidSelection =
      currentIspId !== '' && isps.some(isp => isp.isp_id === Number(currentIspId));
    if (hasValidSelection) {
      void ensureIspBundle(Number(currentIspId));
      return;
    }

    const activeIspId = defaultActiveIspId(isps);
    if (activeIspId === '') return;
    setPanelDraft(prev => ({
      ...prev,
      isp_id: activeIspId,
      outcome_id: '',
      experience_id: '',
      support_id: '', support_ids: [],
    }));
    void ensureIspBundle(activeIspId);
  }, [servicesDialog, ispsByClient, visitClients, panelDraft.isp_id, ensureIspBundle]);

  const saveServicesDialog = () => {
    if (!servicesDialog) return;
    if (panelDraft.service_id === '' || panelDraft.isp_id === '') {
      setShowServiceErrors(true);
      setError('Select service and ISP.');
      return;
    }
    const bundle = ispBundleByIsp[Number(panelDraft.isp_id)] || {
      outcomes: [],
      experiences: [],
      supports: [],
      ispServices: [],
    };
    const isp = deriveIspId(panelDraft, bundle);
    if (!isp) {
      setShowServiceErrors(true);
      setError('Select outcome, experience, or support linked to an ISP, or pick an ISP.');
      return;
    }
    setShowServiceErrors(false);
    const idx = servicesDialog.clientIndex;
    setSegments(prev => {
      const nextClients = [...(prev[0]?.clients ?? [])];
      nextClients[idx] = { ...panelDraft, isp_id: isp };
      return [{ ...(prev[0] ?? { startTime: '', endTime: '', clients: [] }), clients: nextClients }];
    });
    setServicesDialog(null);
    setError(null);
  };

  const openBehaviorDialog = (index: number) => {
    const row = visitClients[index];
    if (!row) return;
    setPanelDraft({ ...row });
    setBehaviorDialog({ clientIndex: index });
    setError(null);
  };

  const saveBehaviorDialog = () => {
    if (!behaviorDialog) return;
    const uiErr = validateUiDraft(panelDraft.ui);
    if (uiErr) {
      setShowBehaviorErrors(true);
      setError(uiErr);
      return;
    }
    setShowBehaviorErrors(false);
    const idx = behaviorDialog.clientIndex;
    setSegments(prev => {
      const nextClients = [...(prev[0]?.clients ?? [])];
      nextClients[idx] = { ...nextClients[idx], ...panelDraft };
      return [{ ...(prev[0] ?? { startTime: '', endTime: '', clients: [] }), clients: nextClients }];
    });
    setBehaviorDialog(null);
    setError(null);
  };

  const renderServicesFields = (form: DraftClientLine, onChange: (patch: Partial<DraftClientLine>) => void) => {
    const cid = form.client_id === '' ? 0 : Number(form.client_id);
    const services = cid ? nonTxForClient(cid) : [];
    const isps = cid ? ispsByClient[cid] || [] : [];
    const ispId = form.isp_id === '' ? 0 : Number(form.isp_id);
    const bundle = ispId ? ispBundleByIsp[ispId] : undefined;
    const serviceMissing = (showServiceErrors || showErrors) && form.service_id === '';
    const ispMissing = (showServiceErrors || showErrors) && form.isp_id === '';

    return (
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required error={serviceMissing}>
            <InputLabel>Service</InputLabel>
            <Select
              label="Service"
              value={form.service_id === '' ? '' : form.service_id}
              onChange={e => {
                const v = e.target.value === '' ? '' : Number(e.target.value);
                onChange({ service_id: v });
              }}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {services.map(s => (
                <MenuItem key={String(s.service_id)} value={s.service_id as number}>
                  {[s.code, s.service_unit].filter(Boolean).join(' | ')}
                  {isOvertimeService(Number(s.service_id)) ? ' (OT code)' : ''}
                </MenuItem>
              ))}
            </Select>
            {serviceMissing && <FormHelperText>Service is required</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required disabled={!cid} error={ispMissing}>
            <InputLabel>ISP</InputLabel>
            <Select
              label="ISP"
              value={form.isp_id === '' ? '' : form.isp_id}
              onChange={e => {
                const v = e.target.value === '' ? '' : Number(e.target.value);
                onChange({ isp_id: v, outcome_id: '', experience_id: '', support_id: '', support_ids: [] });
                if (v !== '' && v) void ensureIspBundle(v);
              }}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {isps.map(isp => (
                <MenuItem key={isp.isp_id} value={isp.isp_id}>
                  #{isp.isp_id}
                  {isp.description ? ` — ${isp.description}` : ''}
                  {isp.active_status === false ? ' (Inactive)' : ''}
                </MenuItem>
              ))}
            </Select>
            {ispMissing && <FormHelperText>ISP is required</FormHelperText>}
          </FormControl>
        </Grid>
        {!!bundle && (
          <>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Outcome</InputLabel>
                <Select
                  label="Outcome"
                  value={form.outcome_id === '' ? '' : form.outcome_id}
                  onChange={e => {
                    const o = e.target.value === '' ? '' : Number(e.target.value);
                    const chosen = bundle.outcomes.find(x => x.outcome_id === o);
                    onChange({
                      outcome_id: o,
                      isp_id: chosen?.isp_id || form.isp_id,
                      experience_id: '',
                      support_id: '', support_ids: [],
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {bundle.outcomes
                    .filter(
                      o =>
                        (o.active_status !== false || o.outcome_id === form.outcome_id) &&
                        (o.isp_id == null || o.isp_id === ispId)
                    )
                    .map(o => (
                      <MenuItem key={o.outcome_id} value={o.outcome_id}>
                        {o.outcome_title || o.outcome_id}
                        {o.active_status === false ? ' (inactive)' : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Experience</InputLabel>
                <Select
                  label="Experience"
                  value={form.experience_id === '' ? '' : form.experience_id}
                  onChange={e => {
                    const ex = e.target.value === '' ? '' : Number(e.target.value);
                    const ch = bundle.experiences.find(x => x.experience_id === ex);
                    onChange({ experience_id: ex, isp_id: ch?.isp_id || form.isp_id, support_id: '', support_ids: [] });
                  }}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {bundle.experiences
                    .filter(
                      ex =>
                        (ex.active_status !== false || ex.experience_id === form.experience_id) &&
                        ex.isp_id === ispId &&
                        (!form.outcome_id || ex.outcome_id === form.outcome_id)
                    )
                    .map(ex => (
                      <MenuItem key={ex.experience_id} value={ex.experience_id}>
                        {ex.experience_title || ex.experience_id}
                        {ex.active_status === false ? ' (inactive)' : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Supports</InputLabel>
                <Select
                  multiple
                  label="Supports"
                  value={form.support_ids || []}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map(id => {
                        const support = bundle.supports.find(x => x.support_id === id);
                        return <Chip key={id} size="small" label={support?.support_title || id} />;
                      })}
                    </Box>
                  )}
                  onChange={e => {
                    const raw = e.target.value;
                    const ids = (typeof raw === 'string' ? raw.split(',') : raw)
                      .map(value => Number(value))
                      .filter(id => id > 0);
                    const ch = bundle.supports.find(x => x.support_id === ids[0]);
                    onChange({
                      support_ids: ids,
                      support_id: ids[0] || '',
                      isp_id: ch?.isp_id || form.isp_id,
                    });
                  }}
                >
                  {(() => {
                    const sid = form.service_id ? Number(form.service_id) : 0;
                    const selected = form.support_ids || [];
                    const m =
                      sid && ispId ? bundle.ispServices.find(x => x.isp_id === ispId && x.service_id === sid) : null;
                    const sups = bundle.supports.filter(
                      su =>
                        (su.active_status !== false || selected.includes(su.support_id)) &&
                        su.isp_id === ispId
                    );
                    const list = m
                      ? sups.filter(su => su.isp_service_id === m.isp_service_id || selected.includes(su.support_id))
                      : sups;
                    return list.map(su => (
                      <MenuItem key={su.support_id} value={su.support_id}>
                        {su.support_title || su.support_id}
                        {su.active_status === false ? ' (inactive)' : ''}
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Visit type</InputLabel>
            <Select
              value={form.visit_type || 'home'}
              label="Visit type"
              onChange={e => onChange({ visit_type: e.target.value as string })}
            >
              <MenuItem value="home">Home</MenuItem>
              <MenuItem value="community">Community</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const renderBehaviorFields = (form: DraftClientLine, onChange: (patch: Partial<DraftClientLine>) => void) => (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <TextField
          size="small"
          fullWidth
          label="Behavior notes"
          value={form.behavior_notes}
          onChange={e => onChange({ behavior_notes: e.target.value })}
          multiline
          rows={2}
        />
      </Grid>
      <UiIncidentFields
        showErrors={showBehaviorErrors || showErrors}
        value={form.ui}
        onChange={patch => onChange({ ui: { ...form.ui, ...patch } })}
      />
    </Grid>
  );

  const buildPayloads = (): { rows: VisitRowPayload[]; error?: string } => {
    const pid = effectiveProvider;
    if (!pid) return { rows: [], error: 'Provider is required.' };
    if (!visitDate) return { rows: [], error: 'Visit date is required.' };
    const out: VisitRowPayload[] = [];
    let flatIndex = 0;
    for (let si = 0; si < segments.length; si++) {
      const seg = segments[si];
      const st = parseFlexibleTimeInput(seg.startTime);
      const en = parseFlexibleTimeInput(seg.endTime);
      if (!st || !en) return { rows: [], error: 'Enter start and end times (e.g. 1:30 PM).' };
      const ss = toMysqlDateTime(visitDate, st);
      const ee = toMysqlDateTime(visitDate, en);
      if (!ss || !ee) return { rows: [], error: 'Invalid date or time.' };
      if (new Date(ee) <= new Date(ss)) return { rows: [], error: 'End time must be after start time.' };
      const n = seg.clients.length;
      if (n === 0) return { rows: [], error: 'Add at least one client.' };
      const clientIds = new Set<number>();
      for (const line of seg.clients) {
        const cid = Number(line.client_id);
        if (clientIds.has(cid)) {
          return { rows: [], error: 'Each client can only appear once on this visit.' };
        }
        clientIds.add(cid);
        const bundle = ispBundleByIsp[Number(line.isp_id)] || {
          outcomes: [],
          experiences: [],
          supports: [],
          ispServices: [],
        };
        if (!hasServices(line, bundle)) {
          const cl = clients.find(x => Number(x.client_id) === cid);
          const name = cl ? `${cl.first_name} ${cl.last_name}` : `Client ${cid}`;
          return { rows: [], error: `${name} needs services configured before saving.` };
        }
      }
      const ratio = `1:${n}`;
      const dur = durationSeconds(ss, ee);
      for (const line of seg.clients) {
        const bundle = ispBundleByIsp[Number(line.isp_id)] || {
          outcomes: [],
          experiences: [],
          supports: [],
          ispServices: [],
        };
        const isp = deriveIspId(line, bundle);
        if (!isp) return { rows: [], error: 'Each line needs a valid ISP (or outcome / experience / support selection).' };
        const visitCreate: VisitRowPayload = {
          provider_id: pid,
          client_id: Number(line.client_id),
          scheduled_start: ss,
          scheduled_end: ee,
          visit_date: visitDate,
          duration: dur,
          status,
          service_id: Number(line.service_id),
          ratio,
          visit_type: line.visit_type || undefined,
          isp_id: isp,
          outcome_id: line.outcome_id === '' ? undefined : Number(line.outcome_id),
          experience_id: line.experience_id === '' ? undefined : Number(line.experience_id),
          support_ids: line.support_ids,
          support_id: line.support_ids[0] || undefined,
          notes: flatIndex === 0 ? (headerNotes || undefined) : undefined,
          behavior_notes: line.behavior_notes || undefined,
          unusual_incident_flag: line.ui.has_ui,
          unusual_incident_type_id: 0,
          major_unusual_incident_flag: line.ui.has_ui && line.ui.mui,
          ui: line.ui,
        };
        if (line.visit_id) visitCreate.visit_id = line.visit_id;
        out.push(visitCreate);
        flatIndex += 1;
      }
    }
    return { rows: out };
  };

  const saveEdit = async (desired: VisitRowPayload[]): Promise<number[]> => {
    if (!editingVisit) return [];
    const parentId = editingVisit.visit_parent_id;
    const parent = (await api.visitParents.get(parentId)) as VisitParentWithVisits;
    const existing = [...(parent.visits || [])].sort((a, b) => Number(a.visit_id) - Number(b.visit_id));
    const n = desired.length;
    const m = existing.length;
    const visitIds: number[] = [];
    for (let i = 0; i < Math.min(m, n); i++) {
      const d = desired[i];
      const upd: VisitUpdate = {
        scheduled_start: d.scheduled_start,
        scheduled_end: d.scheduled_end,
        visit_date: d.visit_date,
        duration: d.duration,
        status: d.status,
        service_id: d.service_id,
        ratio: d.ratio,
        visit_type: d.visit_type,
        isp_id: d.isp_id,
        outcome_id: d.outcome_id,
        experience_id: d.experience_id,
        support_id: d.support_id,
        support_ids: d.support_ids,
        notes: d.notes,
        behavior_notes: d.behavior_notes,
        unusual_incident_flag: d.ui?.has_ui ?? false,
        unusual_incident_type_id: 0,
        major_unusual_incident_flag: Boolean(d.ui?.has_ui && d.ui?.mui),
      };
      await api.visits.update(existing[i].visit_id, upd);
      await syncUiForVisit(adminUiApi, d.ui, {
        provider_id: Number(d.provider_id),
        client_id: Number(d.client_id),
        visit_id: Number(existing[i].visit_id),
      });
      visitIds.push(Number(existing[i].visit_id));
    }
    for (let i = m - 1; i >= n; i--) {
      await api.visits.delete(existing[i].visit_id);
    }
    for (let i = m; i < n; i++) {
      const d = desired[i];
      const { visit_id, ui, ...rest } = d;
      const created = await api.visits.create({ ...rest, visit_parent_id: parentId });
      if (created?.visit_id) {
        await syncUiForVisit(adminUiApi, ui, {
          provider_id: Number(d.provider_id),
          client_id: Number(d.client_id),
          visit_id: Number(created.visit_id),
        });
        visitIds.push(Number(created.visit_id));
      } else {
        visitIds.push(0);
      }
    }
    return visitIds;
  };

  const finishAfterVisitSave = async (link: VisitTransportationLink | null) => {
    if (transportDraft?.length && link) {
      try {
        const desiredRows = transportDraft.map(row => {
          const cid = Number(row.client_id);
          const next: Record<string, unknown> = { ...row };
          next.provider_id = link.providerId;
          next.visit_id = link.visitIdByClientId[cid] ?? (row.visit_id as number | null) ?? null;
          return next;
        });
        const rowPayload = (row: Record<string, unknown>) => {
          const payload = { ...row };
          delete payload.transportation_id;
          return payload;
        };
        if (linkedTransportParentId) {
          const parent = (await api.transportationParents.get(
            linkedTransportParentId
          )) as TransportationParentWithTransportations;
          const existing = [...(parent.transportations || [])].sort(
            (a, b) => Number(a.transportation_id) - Number(b.transportation_id)
          );
          const n = desiredRows.length;
          const m = existing.length;
          for (let i = 0; i < Math.min(m, n); i++) {
            await api.transportation.update(existing[i].transportation_id, {
              ...rowPayload(desiredRows[i]),
              transportation_parent_id: linkedTransportParentId,
            });
          }
          for (let i = m - 1; i >= n; i--) {
            await api.transportation.delete(existing[i].transportation_id);
          }
          for (let i = m; i < n; i++) {
            await api.transportation.create({
              ...rowPayload(desiredRows[i]),
              transportation_parent_id: linkedTransportParentId,
            });
          }
        } else {
          const parent = (await api.transportationParents.create({ provider_id: link.providerId })) as {
            transportation_parent_id: number;
          };
          for (const row of desiredRows) {
            await api.transportation.create({
              ...rowPayload(row),
              transportation_parent_id: parent.transportation_parent_id,
            });
          }
        }
      } catch (e: unknown) {
        const ex = e as { message?: string; response?: { data?: { detail?: string } } };
        setError(
          ex?.response?.data?.detail ||
            ex?.message ||
            'Visit saved, but transportation could not be saved.'
        );
        setTransportDraft(null);
        setLinkedTransportParentId(null);
        onSaved();
        return;
      }
    }
    setTransportDraft(null);
    setLinkedTransportParentId(null);
    onSaved();
    performClose();
  };

  const handleTransportDraftSave = (rows: Record<string, unknown>[]) => {
    setTransportDraft(rows);
    setTransportDialogOpen(false);
    setTransportPrefill(null);
  };

  const handleTransportEntryClose = () => {
    setTransportDialogOpen(false);
    setTransportPrefill(null);
  };

  const openTransportationDialog = () => {
    setError(null);
    if (!effectiveProvider) {
      setError('Select a provider before adding transportation.');
      return;
    }
    const clientIds = Array.from(
      new Set(
        visitClients
          .map(c => Number(c.client_id))
          .filter(id => Number.isFinite(id) && id > 0)
      )
    );
    const seg = segments[0];
    const st = seg ? parseFlexibleTimeInput(seg.startTime) : null;
    const en = seg ? parseFlexibleTimeInput(seg.endTime) : null;
    const scheduledStart = st && visitDate ? toMysqlDateTime(visitDate, st) || undefined : undefined;
    const scheduledEnd = en && visitDate ? toMysqlDateTime(visitDate, en) || undefined : undefined;
    const visitIdByClientId: Record<number, number> = {};
    visitClients.forEach(c => {
      const cid = Number(c.client_id);
      if (cid > 0 && c.visit_id) visitIdByClientId[cid] = Number(c.visit_id);
    });
    setTransportPrefill({
      visitIdByClientId,
      providerId: Number(effectiveProvider),
      transportationDate: visitDate || easternTodayYmd(),
      scheduledStart,
      scheduledEnd,
      clientIds,
    });
    setTransportSession(s => s + 1);
    setTransportDialogOpen(true);
  };

  const trySingleCreateFlow = async (createData: VisitCreate, uiDraft?: UiDraft) => {
    const service = await api.services.get(Number(createData.service_id));
    if (service.ot_id && createData.scheduled_start) {
      const visitDateStr = createData.scheduled_start.trim();
      try {
        const wh = await api.visits.getWeeklyHours(Number(createData.provider_id), visitDateStr);
        const th = readWeeklyTotal(wh);
        if (th >= 40) {
          if (Number(createData.service_id) !== Number(service.ot_id)) {
            setPendingServiceId(service.ot_id as number);
            setPendingSingleCreate(createData);
            setOtOpen(true);
            return;
          }
        }
      } catch {
        /* continue */
      }
    }
    if (service.service_unit === 'Hour' || service.service_unit === '15-minute') {
      if (createData.scheduled_start && createData.scheduled_end) {
        const vh = visitHours(createData.scheduled_start, createData.scheduled_end);
        try {
          const whR = await api.visits.getWeeklyHours(Number(createData.provider_id), createData.scheduled_start.trim());
          const wtotal = readWeeklyTotal(whR);
          if (vh + wtotal > 40 && service.ot_id) {
            setPendingSingleCreate(createData);
            setSplitOpen(true);
            return;
          }
        } catch {
          /* */
        }
      }
    }
    const parent = (await api.visitParents.create({ provider_id: Number(createData.provider_id) })) as { visit_parent_id: number };
    const created = await api.visits.create({ ...createData, visit_parent_id: parent.visit_parent_id });
    if (uiDraft && created?.visit_id) {
      await syncUiForVisit(adminUiApi, uiDraft, {
        provider_id: Number(createData.provider_id),
        client_id: Number(createData.client_id),
        visit_id: Number(created.visit_id),
      });
    }
    await finishAfterVisitSave(
      buildVisitTransportLink([createData], [created?.visit_id != null ? Number(created.visit_id) : null])
    );
  };

  const runSplit = async () => {
    if (!pendingSingleCreate || savingRef.current) return;
    setSplitOpen(false);
    savingRef.current = true;
    setLoading(true);
    try {
      const visitData = pendingSingleCreate;
      const service = await api.services.get(Number(visitData.service_id));
      if (!service.ot_id) {
        setError('Service has no overtime variant.');
        return;
      }
      const vh = visitHours(visitData.scheduled_start!, visitData.scheduled_end!);
      const whR = await api.visits.getWeeklyHours(Number(visitData.provider_id), visitData.scheduled_start!.trim());
      const wtot = readWeeklyTotal(whR);
      const hoursRemaining = 40 - wtot;
      const regH = Math.max(0, Math.min(hoursRemaining, vh));
      const otH = vh - regH;
      if (regH < 0.01 || otH < 0.01) {
        setError('Could not split visit (hours).');
        return;
      }
      const start = new Date(visitData.scheduled_start!);
      const end = new Date(visitData.scheduled_end!);
      const totalMs = end.getTime() - start.getTime();
      const regEnd = new Date(start.getTime() + (totalMs * regH) / vh);
      const regular: VisitCreate = { ...visitData, scheduled_start: formatDateForMySQL(start), scheduled_end: formatDateForMySQL(regEnd) };
      const ot: VisitCreate = {
        ...visitData,
        service_id: service.ot_id as number,
        scheduled_start: formatDateForMySQL(regEnd),
        scheduled_end: formatDateForMySQL(end),
        notes: `Overtime portion split from a longer visit.`,
        behavior_notes: undefined,
      };
      const rw = await runSingleVisitOverlapCheck(regular, { excludeVisitId: undefined });
      if (rw) {
        setError(rw);
        return;
      }
      const ow = await runSingleVisitOverlapCheck(ot, { excludeVisitId: undefined });
      if (ow) {
        setError(ow);
        return;
      }
      const parent = (await api.visitParents.create({ provider_id: Number(visitData.provider_id) })) as {
        visit_parent_id: number;
      };
      const createdRegular = await api.visits.create({ ...regular, visit_parent_id: parent.visit_parent_id });
      await api.visits.create({ ...ot, visit_parent_id: parent.visit_parent_id });
      setPendingSingleCreate(null);
      await finishAfterVisitSave(
        buildVisitTransportLink(
          [regular],
          [createdRegular?.visit_id != null ? Number(createdRegular.visit_id) : null]
        )
      );
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const built = buildPayloads();
      if (built.error) {
        setShowErrors(true);
        setError(built.error);
        return;
      }
      const desired = built.rows;
      if (desired.length === 0) {
        setError('Nothing to save.');
        return;
      }
      for (const row of desired) {
        const uiErr = validateUiDraft(row.ui);
        if (uiErr) {
          setShowErrors(true);
          setShowBehaviorErrors(true);
          setError(uiErr);
          return;
        }
      }
      const overlap = await runVisitPayloadOverlapCheck(desired, {
        editingVisit,
        getVisitParent: id => api.visitParents.get(id) as Promise<VisitParentWithVisits>,
      });
      if (overlap) {
        setError(overlap);
        return;
      }
      if (editingVisit) {
        const visitIds = await saveEdit(desired);
        await finishAfterVisitSave(buildVisitTransportLink(desired, visitIds));
        return;
      }
      if (desired.length === 1) {
        const { ui, visit_id: _vid, ...single } = desired[0];
        await trySingleCreateFlow(single as VisitCreate, ui);
        return;
      }
      const parent = (await api.visitParents.create({ provider_id: Number(desired[0].provider_id) })) as { visit_parent_id: number };
      const visitIds: number[] = [];
      for (const row of desired) {
        const { visit_id: _v, ui, ...r } = row;
        const created = await api.visits.create({ ...r, visit_parent_id: parent.visit_parent_id });
        if (created?.visit_id) {
          await syncUiForVisit(adminUiApi, ui, {
            provider_id: Number(row.provider_id),
            client_id: Number(row.client_id),
            visit_id: Number(created.visit_id),
          });
          visitIds.push(Number(created.visit_id));
        } else {
          visitIds.push(0);
        }
      }
      await finishAfterVisitSave(buildVisitTransportLink(desired, visitIds));
    } catch (e: unknown) {
      const ex = e as { message?: string; response?: { data?: { detail?: string } } };
      setError(ex?.response?.data?.detail || ex?.message || 'Save failed.');
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  const performClose = () => {
    onClose();
  };

  const requestAbandon = () => {
    setAbandonOpen(true);
  };

  const handleConfirmAbandon = () => {
    setAbandonOpen(false);
    performClose();
  };

  const handleCancelAbandon = () => {
    setAbandonOpen(false);
  };

  const handleMainDialogClose = (_: object, reason?: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      requestAbandon();
    }
  };

  const handleOtUse = async () => {
    if (!pendingSingleCreate || !pendingServiceId || savingRef.current) return;
    setOtOpen(false);
    savingRef.current = true;
    setLoading(true);
    try {
      const withOt = { ...pendingSingleCreate, service_id: pendingServiceId };
      const o = await runSingleVisitOverlapCheck(withOt, { excludeVisitId: undefined });
      if (o) {
        setError(o);
        return;
      }
      await trySingleCreateFlow(withOt);
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
      <>
      <Dialog open={open && !transportDialogOpen} onClose={handleMainDialogClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingVisit ? 'Edit Visit' : 'Add Visit'}
          <IconButton onClick={requestAbandon} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {multiSegmentWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This visit has multiple time segments. Only the first segment is shown. Saving will remove visits in other
              time ranges.
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {!fixedProviderId ? (
              <FormControl fullWidth required error={providerMissing}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={providerId === '' ? '' : providerId}
                  label="Provider"
                  onChange={e => setProviderId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <MenuItem value="">
                    <em>Select provider</em>
                  </MenuItem>
                  {providers.map(p => (
                    <MenuItem key={String(p.provider_id)} value={p.provider_id as number}>
                      {p.first_name} {p.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {providerMissing && <FormHelperText>Provider is required</FormHelperText>}
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label="Provider"
                value={providerLabel}
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: LINE_BG, borderWidth: 2 },
                  },
                }}
              />
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AppDateField
                  label="Visit date"
                  required
                  value={visitDate || null}
                  onChange={v => setVisitDate(v || '')}
                  error={dateMissing}
                  helperText={dateMissing ? 'Visit date is required' : undefined}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={status} label="Status" onChange={e => setStatus(e.target.value as string)}>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Start
                </Typography>
                <Button
                  size="small"
                  onClick={() => setTimeToNow('start')}
                  sx={{ textTransform: 'none', minWidth: 0, py: 0, fontWeight: 600, color: LINE_BG }}
                >
                  Now
                </Button>
              </Box>
              <VisitTimeField
                label="Start time"
                visitDate={visitDate}
                value={seg.startTime}
                onChange={setStartTime}
                errorText={
                  startTimeInvalid
                    ? seg.startTime.trim()
                      ? 'Enter a valid start time'
                      : 'Start time is required'
                    : undefined
                }
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  End
                </Typography>
                <Button
                  size="small"
                  onClick={() => setTimeToNow('end')}
                  sx={{ textTransform: 'none', minWidth: 0, py: 0, fontWeight: 600, color: LINE_BG }}
                >
                  Now
                </Button>
              </Box>
              <VisitTimeField
                label="End time"
                visitDate={visitDate}
                value={seg.endTime}
                onChange={setEndTime}
                errorText={endTimeMessage}
              />
            </Box>

            {visitClients.length > 0 && (
              <Stack spacing={2}>
                {visitClients.map((row, idx) => {
                  const bundle = ispBundleByIsp[Number(row.isp_id)] || undefined;
                  const servicesFilled = hasServices(row, bundle);
                  const behaviorFilled = hasBehavior(row);
                  const servicesMissing = showErrors && !servicesFilled;
                  const behaviorMissing = showErrors && !!validateUiDraft(row.ui);
                  return (
                    <Box
                      key={row.visit_id != null ? `v-${row.visit_id}` : `c-${String(row.client_id)}-${idx}`}
                      sx={{
                        border: '1px solid',
                        borderColor: servicesMissing || behaviorMissing ? 'error.main' : 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: LINE_BG,
                          color: '#fff',
                          px: 2,
                          py: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography fontWeight={600}>{clientName(row.client_id)}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeClient(idx)}
                          aria-label="Remove client"
                          sx={{ color: 'rgba(255,255,255,0.85)' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ px: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography fontWeight={500} color={servicesMissing ? 'error' : 'inherit'}>
                            Services{servicesMissing ? ' — required' : ''}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => openServicesDialog(idx)}
                            sx={actionBtnSx(servicesFilled)}
                          >
                            {servicesFilled ? 'Change' : 'Add'}
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1.5,
                          }}
                        >
                          <Typography fontWeight={500} color={behaviorMissing ? 'error' : 'inherit'}>
                            Behavior{behaviorMissing ? ' — required fields missing' : ''}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => openBehaviorDialog(idx)}
                            sx={actionBtnSx(behaviorFilled)}
                          >
                            {behaviorFilled ? 'Change' : 'Add'}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}

            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddClient}
              disabled={!effectiveProvider}
              sx={{
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: LINE_BG,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1E3452', boxShadow: 'none' },
              }}
            >
              Add Client
            </Button>
            {clientsMissing && (
              <Typography variant="body2" color="error">
                Add at least one client.
              </Typography>
            )}

            {visitClients.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Ratio is set automatically (e.g. {visitClients.length} client{visitClients.length === 1 ? '' : 's'} = 1:
                {visitClients.length}).
              </Typography>
            )}

            <TextField
              fullWidth
              label="Notes"
              value={headerNotes}
              onChange={e => setHeaderNotes(e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Box sx={{ py: 0.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography fontWeight={500}>Transportation</Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={openTransportationDialog}
                  disabled={!effectiveProvider}
                  sx={actionBtnSx(hasTransportation)}
                >
                  {hasTransportation ? 'Edit' : 'Add'}
                </Button>
              </Box>
              {transportSummary && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {transportSummary}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={requestAbandon}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ backgroundColor: '#2D4B73' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {transportDialogOpen && transportPrefill && (
        <Transportation
          key={transportSession}
          entryVariant="addDialogOnly"
          selectedProvider={transportPrefill.providerId}
          visitLink={transportPrefill}
          initialDraftRows={transportDraft}
          onDraftSave={handleTransportDraftSave}
          onAddEntryClose={handleTransportEntryClose}
        />
      )}

      <Dialog open={addClientOpen} onClose={() => setAddClientOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add client</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={addClientPick === '' ? '' : addClientPick}
              onChange={e => {
                const v = e.target.value;
                setAddClientPick(v === '' ? '' : Number(v));
              }}
            >
              <MenuItem value="">
                <em>Select client</em>
              </MenuItem>
              {clients.map(c => (
                <MenuItem
                  key={String(c.client_id)}
                  value={c.client_id as number}
                  disabled={visitClients.some(d => d.client_id !== '' && Number(d.client_id) === Number(c.client_id))}
                >
                  {c.first_name} {c.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddClientOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmAddClient} sx={{ backgroundColor: '#2D4B73' }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!servicesDialog}
        onClose={() => setServicesDialog(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {servicesDialog ? `Services — ${clientName(visitClients[servicesDialog.clientIndex]?.client_id ?? '')}` : 'Services'}
        </DialogTitle>
        <DialogContent dividers>{renderServicesFields(panelDraft, updatePanelDraft)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setServicesDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveServicesDialog} sx={{ backgroundColor: '#2D4B73' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!behaviorDialog}
        onClose={() => setBehaviorDialog(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {behaviorDialog
            ? `Behavior — ${clientName(visitClients[behaviorDialog.clientIndex]?.client_id ?? '')}`
            : 'Behavior'}
        </DialogTitle>
        <DialogContent dividers>{renderBehaviorFields(panelDraft, updatePanelDraft)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setBehaviorDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveBehaviorDialog} sx={{ backgroundColor: '#2D4B73' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={splitOpen} onClose={() => setSplitOpen(false)}>
        <DialogTitle>Split for overtime?</DialogTitle>
        <DialogContent>
          <Typography>
            This visit would exceed 40 hours for the week. Split into regular and overtime portions?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSplitOpen(false);
              setSplitConfirmOpen(true);
            }}
          >
            No thanks
          </Button>
          <Button variant="contained" onClick={runSplit}>
            Yes, split
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={splitConfirmOpen} onClose={() => setSplitConfirmOpen(false)}>
        <DialogTitle>Continue without splitting?</DialogTitle>
        <DialogContent>
          <Typography>Creating this visit as a single entry may affect billing. Continue anyway?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSplitConfirmOpen(false);
              setSplitOpen(true);
            }}
          >
            Go back
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={loading}
            onClick={async () => {
              if (!pendingSingleCreate || savingRef.current) return;
              setSplitConfirmOpen(false);
              savingRef.current = true;
              setLoading(true);
              try {
                const parent = (await api.visitParents.create({ provider_id: Number(pendingSingleCreate.provider_id) })) as {
                  visit_parent_id: number;
                };
                const created = await api.visits.create({
                  ...pendingSingleCreate,
                  visit_parent_id: parent.visit_parent_id,
                });
                const link = buildVisitTransportLink(
                  [pendingSingleCreate],
                  [created?.visit_id != null ? Number(created.visit_id) : null]
                );
                setPendingSingleCreate(null);
                await finishAfterVisitSave(link);
              } catch (e: unknown) {
                const ex = e as { message?: string };
                setError(ex?.message || 'Save failed');
              } finally {
                savingRef.current = false;
                setLoading(false);
              }
            }}
          >
            Continue anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={otOpen} onClose={() => setOtOpen(false)}>
        <DialogTitle>Use overtime service code?</DialogTitle>
        <DialogContent>
          <Typography>
            This provider is already at or over 40 hours. Use the overtime service for this visit?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleOtUse}>
            Use OT service
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={abandonOpen} onClose={handleCancelAbandon}>
        <DialogTitle>Abandon visit?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to abandon this visit entry? All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAbandon}>Cancel</Button>
          <Button onClick={handleConfirmAbandon} variant="contained" color="error">
            Abandon visit
          </Button>
        </DialogActions>
      </Dialog>
      </>
  );
};

export default GroupVisitForm;
